import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminCouponService } from '../../services/api';
import { adminUserService } from '../../services/adminUserService';
import { ADMIN_LIST_PAGE_LIMIT } from '../utils/adminOrdersBulkFetch';
import { extractCreatorId } from '../utils/authUtils';
import './PromoCodesPanel.css';

const emptyForm = () => ({
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  maxUses: '100',
  perUserLimit: '1',
  minOrderAmount: '0',
  maxDiscountAmount: '',
  startsAt: '',
  expiresAt: '',
  isActive: true,
  shopkeeperScope: 'all',
  shopkeeperId: '',
});

function parseAdminError(err) {
  if (!err?.message) return 'Something went wrong';
  try {
    const o = JSON.parse(err.message);
    return o.error || o.message || err.message;
  } catch {
    return err.message;
  }
}

function normalizeCouponList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  return res.data || res.coupons || res.results || [];
}

function couponId(c) {
  return c?._id || c?.id || '';
}

function resolveCouponShopkeeperId(c) {
  if (!c) return '';
  const ref = c.shopkeeperId ?? c.shopkeeper ?? c.createdForShopkeeperId;
  return ref ? String(extractCreatorId(ref) || ref) : '';
}

function resolveCouponScope(c) {
  const raw = c?.scope ?? c?.couponScope;
  if (raw === 'shopkeeper' || resolveCouponShopkeeperId(c)) return 'shopkeeper';
  return 'all';
}

function shopkeeperLabelFromCoupon(c, teamById) {
  if (resolveCouponScope(c) !== 'shopkeeper') return 'All (whole store)';
  const id = resolveCouponShopkeeperId(c);
  const populated = c?.shopkeeper;
  if (populated && typeof populated === 'object') {
    return populated.name || populated.email || id || 'Shopkeeper';
  }
  const u = teamById.get(id);
  return u?.name || u?.email || id || 'Shopkeeper';
}

const LIST_LIMIT = 50;

const PromoCodesPanel = ({ onNotify }) => {
  const [coupons, setCoupons] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [listPage, setListPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);

  const teamById = useMemo(() => {
    const map = new Map();
    for (const u of teamUsers) {
      const id = u?._id || u?.id;
      if (id) map.set(String(id), u);
    }
    return map;
  }, [teamUsers]);

  const fetchTeamUsers = useCallback(async () => {
    setTeamLoading(true);
    try {
      const acc = [];
      for (let page = 1; page <= 200; page += 1) {
        let res = await adminUserService.getUsers({
          role: 'team',
          limit: ADMIN_LIST_PAGE_LIMIT,
          page,
        });
        let list = res?.success && Array.isArray(res.data) ? res.data : [];
        if (list.length === 0 && page === 1) {
          res = await adminUserService.getUsers({ limit: ADMIN_LIST_PAGE_LIMIT, page: 1 });
          const all = res?.success && Array.isArray(res.data) ? res.data : [];
          list = all.filter((u) => String(u?.role || '').toLowerCase() === 'team');
          acc.push(...list);
          break;
        }
        acc.push(...list);
        if (list.length < ADMIN_LIST_PAGE_LIMIT) break;
      }
      setTeamUsers(acc);
    } catch {
      setTeamUsers([]);
    } finally {
      setTeamLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamUsers();
  }, [fetchTeamUsers]);

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    setListError('');
    const params = {
      page: listPage,
      limit: LIST_LIMIT,
    };
    if (appliedSearch.trim()) params.search = appliedSearch.trim();
    if (statusFilter === 'active') params.isActive = true;
    if (statusFilter === 'inactive') params.isActive = false;

    try {
      const res = await adminCouponService.listCoupons(params);
      if (res?.success === false) {
        setListError(res.message || res.error || 'Could not load promo codes');
        setCoupons([]);
        setPagination(null);
        return;
      }
      setCoupons(normalizeCouponList(res));
      setPagination(res.pagination ?? null);
    } catch (e) {
      setListError(parseAdminError(e));
      setCoupons([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [listPage, appliedSearch, statusFilter]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const applySearchFilters = () => {
    setAppliedSearch(searchInput.trim());
    setListPage(1);
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
    setFormError('');
  };

  const buildPayload = () => {
    const code = String(form.code || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!code) throw new Error('Promo code is required');

    const discountValue = Number(form.discountValue);
    if (!Number.isFinite(discountValue) || discountValue <= 0) {
      throw new Error('Enter a valid discount value');
    }
    if (form.discountType === 'percentage' && discountValue > 100) {
      throw new Error('Percentage cannot exceed 100');
    }

    const maxUses = parseInt(form.maxUses, 10);
    if (!Number.isFinite(maxUses) || maxUses < 1) {
      throw new Error('Quantity (max uses) must be at least 1');
    }

    const perUserLimit = Math.max(1, parseInt(form.perUserLimit, 10) || 1);
    const minOrderAmount = Math.max(0, Number(form.minOrderAmount) || 0);

    const payload = {
      code,
      description: String(form.description || '').trim() || undefined,
      discountType: form.discountType === 'fixed' ? 'fixed' : 'percentage',
      discountValue,
      maxUses,
      perUserLimit,
      minOrderAmount,
      isActive: !!form.isActive,
    };

    if (form.discountType === 'percentage' && form.maxDiscountAmount !== '') {
      const cap = Number(form.maxDiscountAmount);
      if (Number.isFinite(cap) && cap > 0) payload.maxDiscountAmount = cap;
    }

    if (form.startsAt) payload.startsAt = new Date(form.startsAt).toISOString();
    if (form.expiresAt) payload.expiresAt = new Date(form.expiresAt).toISOString();

    const scopeChoice = form.shopkeeperScope === 'shopkeeper' ? 'shopkeeper' : 'all';
    payload.scope = scopeChoice;
    if (scopeChoice === 'shopkeeper') {
      const sid = String(form.shopkeeperId || '').trim();
      if (!sid) throw new Error('Select a shopkeeper for this coupon, or choose All (whole store)');
      payload.shopkeeperId = sid;
    } else {
      payload.shopkeeperId = null;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    let payload;
    try {
      payload = buildPayload();
    } catch (err) {
      setFormError(err.message);
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const res = await adminCouponService.updateCoupon(editingId, payload);
        if (res?.success === false) {
          throw new Error(res.message || res.error || 'Update failed');
        }
        onNotify?.({ type: 'success', text: 'Promo code updated' });
      } else {
        const res = await adminCouponService.createCoupon(payload);
        if (res?.success === false) {
          throw new Error(res.message || res.error || 'Create failed');
        }
        onNotify?.({ type: 'success', text: 'Promo code issued' });
      }
      resetForm();
      await loadCoupons();
    } catch (err) {
      setFormError(parseAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    const id = couponId(c);
    if (!id) return;
    setEditingId(id);
    setFormError('');
    const scope = resolveCouponScope(c);
    setForm({
      code: c.code || '',
      description: c.description || '',
      discountType: c.discountType === 'fixed' ? 'fixed' : 'percentage',
      discountValue: c.discountValue != null ? String(c.discountValue) : '',
      maxUses: c.maxUses != null ? String(c.maxUses) : '100',
      perUserLimit: c.perUserLimit != null ? String(c.perUserLimit) : '1',
      minOrderAmount: c.minOrderAmount != null ? String(c.minOrderAmount) : '0',
      maxDiscountAmount: c.maxDiscountAmount != null ? String(c.maxDiscountAmount) : '',
      startsAt: c.startsAt ? c.startsAt.slice(0, 16) : '',
      expiresAt: c.expiresAt ? c.expiresAt.slice(0, 16) : '',
      isActive: c.isActive !== false,
      shopkeeperScope: scope,
      shopkeeperId: resolveCouponShopkeeperId(c),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (c) => {
    const id = couponId(c);
    if (!id) return;
    if (!window.confirm(`Delete promo code "${c.code}"? This cannot be undone.`)) return;
    try {
      const res = await adminCouponService.deleteCoupon(id);
      if (res?.success === false) {
        throw new Error(res.message || res.error || 'Delete failed');
      }
      onNotify?.({ type: 'success', text: 'Promo code removed' });
      if (editingId === id) resetForm();
      await loadCoupons();
    } catch (err) {
      onNotify?.({ type: 'error', text: parseAdminError(err) });
    }
  };

  const handleToggleActive = async (c) => {
    const id = couponId(c);
    if (!id) return;
    setTogglingId(id);
    try {
      const res = await adminCouponService.updateCoupon(id, { isActive: !c.isActive });
      if (res?.success === false) {
        throw new Error(res.message || res.error || 'Update failed');
      }
      await loadCoupons();
    } catch (err) {
      onNotify?.({ type: 'error', text: parseAdminError(err) });
    } finally {
      setTogglingId(null);
    }
  };

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="promo-admin">
      <div className="promo-admin-intro">
        <h2><i className="fas fa-ticket-alt" /> Promo codes</h2>
        <p>
          Issue discount codes with usage limits and validity. Default scope is the whole store; restrict a code to one
          shopkeeper so it only applies to that creator&apos;s products. Checkout uses{' '}
          <code>POST /api/cart/coupon</code>.
        </p>
      </div>

      {listError && (
        <div className="promo-admin-banner error">
          <i className="fas fa-exclamation-triangle" />
          <div>
            <strong>Could not load codes</strong>
            <p>{listError}</p>
            <small>If this is new, implement the backend routes described in <code>docs/BACKEND_ADMIN_PROMO_COUPONS_API.md</code>.</small>
          </div>
          <button type="button" className="promo-admin-retry" onClick={loadCoupons}>
            Retry
          </button>
        </div>
      )}

      <form className="promo-admin-form" onSubmit={handleSubmit}>
        <div className="promo-admin-form-head">
          <h3>{editingId ? 'Edit promo code' : 'Issue new promo code'}</h3>
          {editingId && (
            <button type="button" className="promo-admin-link-btn" onClick={resetForm}>
              Cancel edit
            </button>
          )}
        </div>

        {formError && <div className="promo-admin-inline-error">{formError}</div>}

        <div className="promo-admin-grid">
          <div className="promo-admin-field">
            <label>Code <span className="req">*</span></label>
            <input
              value={form.code}
              onChange={(e) => updateField('code', e.target.value.toUpperCase())}
              placeholder="e.g. SUMMER25"
              disabled={!!editingId}
              className="promo-admin-input"
            />
            {editingId && <small>Code cannot be changed when editing</small>}
          </div>
          <div className="promo-admin-field span-2">
            <label>Description</label>
            <input
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Internal note or customer-facing text"
              className="promo-admin-input"
            />
          </div>
          <div className="promo-admin-field span-2 promo-admin-field-shopkeeper">
            <label>For shopkeeper</label>
            <div className="promo-admin-shopkeeper-row">
              <select
                value={form.shopkeeperScope}
                onChange={(e) => {
                  const v = e.target.value;
                  updateField('shopkeeperScope', v);
                  if (v === 'all') updateField('shopkeeperId', '');
                }}
                className="promo-admin-input"
              >
                <option value="all">All — whole store (default)</option>
                <option value="shopkeeper">One shopkeeper — their products only</option>
              </select>
              {form.shopkeeperScope === 'shopkeeper' && (
                <select
                  value={form.shopkeeperId}
                  onChange={(e) => updateField('shopkeeperId', e.target.value)}
                  className="promo-admin-input"
                  disabled={teamLoading}
                >
                  <option value="">{teamLoading ? 'Loading team…' : 'Select shopkeeper…'}</option>
                  {teamUsers.map((u) => {
                    const id = String(u._id || u.id || '');
                    const label = u.name || u.email || id;
                    return (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
            <small>
              Shopkeeper-scoped codes apply only when the cart qualifies (that creator&apos;s lines). Discount counts in
              that creator&apos;s Sales after the order is confirmed.
            </small>
          </div>
          <div className="promo-admin-field">
            <label>Discount type <span className="req">*</span></label>
            <select
              value={form.discountType}
              onChange={(e) => updateField('discountType', e.target.value)}
              className="promo-admin-input"
            >
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed amount (PKR)</option>
            </select>
          </div>
          <div className="promo-admin-field">
            <label>Discount value <span className="req">*</span></label>
            <input
              type="number"
              min="0"
              step={form.discountType === 'percentage' ? '1' : '1'}
              value={form.discountValue}
              onChange={(e) => updateField('discountValue', e.target.value)}
              placeholder={form.discountType === 'percentage' ? '15' : '500'}
              className="promo-admin-input"
            />
          </div>
          {form.discountType === 'percentage' && (
            <div className="promo-admin-field">
              <label>Max discount cap (PKR)</label>
              <input
                type="number"
                min="0"
                value={form.maxDiscountAmount}
                onChange={(e) => updateField('maxDiscountAmount', e.target.value)}
                placeholder="Optional cap on % discount"
                className="promo-admin-input"
              />
            </div>
          )}
          <div className="promo-admin-field">
            <label>Total uses (quantity) <span className="req">*</span></label>
            <input
              type="number"
              min="1"
              value={form.maxUses}
              onChange={(e) => updateField('maxUses', e.target.value)}
              className="promo-admin-input"
            />
            <small>How many times this code can be redeemed in total</small>
          </div>
          <div className="promo-admin-field">
            <label>Per-user limit</label>
            <input
              type="number"
              min="1"
              value={form.perUserLimit}
              onChange={(e) => updateField('perUserLimit', e.target.value)}
              className="promo-admin-input"
            />
          </div>
          <div className="promo-admin-field">
            <label>Minimum order (PKR)</label>
            <input
              type="number"
              min="0"
              value={form.minOrderAmount}
              onChange={(e) => updateField('minOrderAmount', e.target.value)}
              className="promo-admin-input"
            />
          </div>
          <div className="promo-admin-field">
            <label>Valid from</label>
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => updateField('startsAt', e.target.value)}
              className="promo-admin-input"
            />
          </div>
          <div className="promo-admin-field">
            <label>Valid until</label>
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) => updateField('expiresAt', e.target.value)}
              className="promo-admin-input"
            />
          </div>
          <div className="promo-admin-field promo-admin-checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
              />
              Active (customers can apply if within dates &amp; limits)
            </label>
          </div>
        </div>

        <div className="promo-admin-actions">
          <button type="submit" className="promo-admin-submit" disabled={saving}>
            {saving ? <><i className="fas fa-spinner fa-spin" /> Saving…</> : editingId ? 'Update promo code' : 'Issue promo code'}
          </button>
        </div>
      </form>

      <div className="promo-admin-table-wrap">
        <div className="promo-admin-table-head">
          <h3>Existing codes</h3>
          <div className="promo-admin-list-toolbar">
            <input
              type="search"
              className="promo-admin-toolbar-input"
              placeholder="Search by code…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearchFilters()}
            />
            <button type="button" className="promo-admin-toolbar-btn" onClick={applySearchFilters}>
              Search
            </button>
            <select
              className="promo-admin-toolbar-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setListPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="promo-admin-loading"><i className="fas fa-spinner fa-spin" /> Loading…</div>
        ) : coupons.length === 0 ? (
          <p className="promo-admin-empty">
            {appliedSearch || statusFilter !== 'all'
              ? 'No promo codes match your filters.'
              : 'No promo codes yet — issue one above.'}
          </p>
        ) : (
          <div className="promo-admin-table-scroll">
            <table className="promo-admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Uses</th>
                  <th>Min order</th>
                  <th>Shopkeeper</th>
                  <th>Valid until</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => {
                  const id = couponId(c);
                  const used = c.usedCount ?? c.used ?? 0;
                  const max = c.maxUses ?? c.quantity ?? '—';
                  return (
                    <tr key={id || c.code}>
                      <td><strong>{c.code}</strong></td>
                      <td>{c.discountType === 'fixed' ? 'Fixed PKR' : '%'}</td>
                      <td>{c.discountValue != null ? c.discountValue : '—'}</td>
                      <td>{used} / {max}</td>
                      <td>{c.minOrderAmount != null ? `PKR ${Number(c.minOrderAmount).toLocaleString()}` : '—'}</td>
                      <td>{shopkeeperLabelFromCoupon(c, teamById)}</td>
                      <td>{c.expiresAt ? new Date(c.expiresAt).toLocaleString() : '—'}</td>
                      <td>
                        <span className={`promo-admin-pill ${c.isActive !== false ? 'on' : 'off'}`}>
                          {c.isActive !== false ? 'Active' : 'Off'}
                        </span>
                      </td>
                      <td className="promo-admin-row-actions">
                        <button
                          type="button"
                          className="promo-admin-icon-btn"
                          title={c.isActive !== false ? 'Deactivate' : 'Activate'}
                          disabled={togglingId === id}
                          onClick={() => handleToggleActive(c)}
                        >
                          <i className={`fas fa-toggle-${c.isActive !== false ? 'on' : 'off'}`} />
                        </button>
                        <button type="button" className="promo-admin-icon-btn" title="Edit" onClick={() => startEdit(c)}>
                          <i className="fas fa-edit" />
                        </button>
                        <button type="button" className="promo-admin-icon-btn danger" title="Delete" onClick={() => handleDelete(c)}>
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && pagination && (pagination.totalPages ?? 1) > 1 && (
          <div className="promo-admin-pager">
            <button
              type="button"
              className="promo-admin-pager-btn"
              disabled={listPage <= 1}
              onClick={() => setListPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="promo-admin-pager-info">
              Page {pagination.page ?? listPage} of {pagination.totalPages}
            </span>
            <button
              type="button"
              className="promo-admin-pager-btn"
              disabled={listPage >= (pagination.totalPages ?? 1)}
              onClick={() =>
                setListPage((p) => Math.min(pagination.totalPages ?? p + 1, p + 1))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoCodesPanel;
